import { LeadSource, QuoteType } from "./Lead";

// Mirrors GPMetals.Core.Models.CallQueue. The enums travel as ints, so the values are pinned to the API's.

export enum CallOutcome {
  Booked = 0,
  CallBack = 1,
  NoAnswer = 2,
  VoicemailLeft = 3,
  PriceTooLow = 4,
  SoldElsewhere = 5,
  NotInterested = 6,
  WrongNumber = 7,
  /** The customer will ring us: the lead leaves the queue without an attempt or a call back. */
  CustomerWillCallBack = 8,
}

export enum CallLeadType {
  HighValue = 0,
  Salvage = 1,
  Local = 2,
  New = 3,
}

export enum UpNextFilter {
  AllDue = 0,
  CallBacks = 1,
  HighValue = 2,
  Salvage = 3,
  Local = 4,
  New = 5,
}

export enum LeadAgeTone {
  Green = 0,
  Amber = 1,
  Red = 2,
}

export enum CallQueueBucket {
  CallBackDue = 0,
  New = 1,
  RetryDue = 2,
  Other = 3,
}

export enum TopUpState {
  NotUsed = 0,
  IncludedInQuote = 1,
  NeedsLookup = 2,
  NoOffer = 3,
  Added = 4,
}

export enum LookupState {
  NotUsed = 0,
  NeedsLookup = 1,
  NoOffer = 2,
  Found = 3,
}

export enum InitialPriceEstimateStatus {
  Queued = 0,
  Running = 1,
  Completed = 2,
  Failed = 3,
}

export interface ICallQueueStats {
  calls: number;
  callTarget: number;
  bookings: number;
  bookingTarget: number;
  conversionPercent: number;
  queueDue: number;
}

export interface IUpNextRow {
  leadId: string;
  vrm: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  quotedPrice: number;
  created: string;
  ageTone: LeadAgeTone;
  bucket: CallQueueBucket;
  leadTypes: CallLeadType[];
  callBackDueAt?: string | null;
  callBackIsMine: boolean;
  attempts: number;
  maxAttempts: number;
  website: LeadSource;
  source?: string | null;
  atfCustomerId?: string | null;
  isServedToMe: boolean;
  /** A colleague is on this lead: listed, but it can't be opened. */
  isLocked: boolean;
  /** Who holds the lead, when it's served to me or locked by a colleague. */
  servedToUserId?: string | null;
  canRelease: boolean;
}

export interface ICallQueueOverview {
  stats: ICallQueueStats;
  upNext: IUpNextRow[];
  moreWaiting: number;
  oldestWaitingSince?: string | null;
  withManager: number;
  pendingApprovals: number;
  canApproveOffers: boolean;
  /** An approver, or anyone while an admin has unlocked the queue. */
  canOpenLeads: boolean;
  isSav: boolean;
  insideCallingHours: boolean;
  /** "hh:mm:ss", London time. */
  callingHoursStart: string;
  callingHoursEnd: string;
  servedLeadId?: string | null;
}

export interface ICallOffer {
  floor?: number | null;
  max: number;
  exportTopUpState: TopUpState;
  exportValue?: number | null;
  exportTopUp: number;
  catTopUpState: TopUpState;
  catValue?: number | null;
  catTopUp: number;
  /** Scrap route only: the initial price estimate as a top-up range. The high end is in the max. */
  partsTopUpState: TopUpState;
  partsValue?: number | null;
  partsTopUpLow: number;
  partsTopUpHigh: number;
  needsValuation: boolean;
  needsPartsEstimate: boolean;
  /** Salvage route only: whether the engine and cat figures (exportValue, catValue) have been fetched. Reference values, not in the max. */
  exportLookupState: LookupState;
  catLookupState: LookupState;
}

export interface IPendingApprovalSummary {
  id: string;
  requestedByUserId: string;
  requestedPrice: number;
  created: string;
}

export interface IObjectionCard {
  title: string;
  body: string;
}

export interface ICallHistoryEntry {
  at: string;
  kind: "quote" | "email" | "call" | "action" | "approval";
  text: string;
  userId?: string | null;
  outcome?: CallOutcome | null;
}

export interface ICallSheet {
  leadId: string;
  vehicleId: string;
  vrm: string;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  quoteType: QuoteType;
  website: LeadSource;
  source?: string | null;
  created: string;
  ageTone: LeadAgeTone;
  leadTypes: CallLeadType[];
  callsMade: number;
  attempts: number;
  maxAttempts: number;
  isServedToMe: boolean;

  contactName?: string | null;
  telephone?: string | null;
  email?: string | null;
  postcode?: string | null;
  atfCustomerId?: string | null;

  onlineQuote: number;
  quotedPrice: number;
  priceChanged: boolean;
  offer: ICallOffer;
  headroom: number;
  bookingLimit: number;
  approvedPrice?: number | null;
  overMaxAllowancePercent: number;
  negotiationStep: number;
  validUntil: string;
  pricePerTonne?: number | null;
  kerbWeightKg: number;

  partsEstimate?: number | null;
  partsEstimateStatus?: InitialPriceEstimateStatus | null;
  salvageValuation?: number | null;

  v5C: boolean;
  keys: boolean;

  callBackDefault: string;
  openCallBackDueAt?: string | null;
  openCallBackAssignedTo?: string | null;
  pendingApproval?: IPendingApprovalSummary | null;

  objections: IObjectionCard[];
  history: ICallHistoryEntry[];
}

export interface ILogCallOutcomeRequest {
  outcome: CallOutcome;
  startedOn?: string | null;
  agreedPrice?: number | null;
  /** On an outcome that doesn't book or close the lead: the price offered, which becomes the quoted price. */
  offeredPrice?: number | null;
  customerFigure?: number | null;
  callBackAt?: string | null;
  assignCallBackToMe?: boolean;
  referredToCustomerId?: string | null;
  notes?: string | null;
  ownerHasV5C?: boolean | null;
  keysPresent?: boolean | null;
  complete?: boolean | null;
  wheelsOn?: boolean | null;
  addressMatches?: boolean | null;
}

export interface IUpdateCallContactRequest {
  telephone?: string | null;
  email?: string | null;
}

export interface IRequestOfferApprovalRequest {
  requestedPrice: number;
  note?: string;
}

export interface IDecideOfferApprovalRequest {
  approve: boolean;
  approvedPrice?: number | null;
  declineReason?: string | null;
}

export interface IPendingApprovalRow {
  id: string;
  leadId: string;
  vrm: string;
  make?: string | null;
  model?: string | null;
  requestedByUserId: string;
  requestedPrice: number;
  max: number;
  quotedPrice: number;
  note?: string | null;
  created: string;
}

export interface ICallBackRow {
  id: string;
  leadId: string;
  vrm: string;
  make?: string | null;
  model?: string | null;
  contactName?: string | null;
  telephone?: string | null;
  quotedPrice: number;
  dueAt: string;
  assignedUserId?: string | null;
  createdByUserId: string;
  note?: string | null;
  isDue: boolean;
}

export interface ICallAgent {
  id: string;
  userId: string;
  dailyCallTarget?: number | null;
  dailyBookingTarget?: number | null;
  canApproveOffers: boolean;
  leadTypes: CallLeadType[];
}

export interface IUpsertCallAgentRequest {
  dailyCallTarget?: number | null;
  dailyBookingTarget?: number | null;
  canApproveOffers: boolean;
  leadTypes: CallLeadType[];
}
